import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Ban,
  Check,
  ExternalLink,
  MapPin,
  Phone,
  Star,
  Truck,
  Wallet,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CompactStatCard, PageHeader, StatsGrid } from "@/components/shared";
import {
  useAdminDeliveryPartnerDetail,
  useApproveDeliveryPartnerMutation,
  useSuspendDeliveryPartnerMutation,
} from "@/hooks/queries";
import { resolveUploadUrl } from "@/services/filesService";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";
import { DeliveryPartnerStatus } from "@/types/enums";

const statusBadge: Record<string, string> = {
  Pending: "bg-warning/10 text-warning border-warning/20",
  Active: "bg-success/10 text-success border-success/20",
  Suspended: "bg-destructive/10 text-destructive border-destructive/20",
  Rejected: "bg-destructive/10 text-destructive border-destructive/20",
};

function formatDate(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border/60 py-2 last:border-0 sm:flex-row sm:justify-between sm:gap-4">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <span className="break-all text-sm font-medium sm:text-right">{value ?? "—"}</span>
    </div>
  );
}

function DocLink({ url, label }: { url?: string; label: string }) {
  if (!url) return <span className="text-muted-foreground">Not uploaded</span>;
  const href = resolveUploadUrl(url);
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
    >
      {label} <ExternalLink className="h-3.5 w-3.5" />
    </a>
  );
}

const AdminDeliveryDetail = () => {
  const { id } = useParams();
  const partnerId = id ? Number(id) : 0;
  const { toast } = useToast();
  const { data: partner, isLoading, refetch } = useAdminDeliveryPartnerDetail(partnerId);
  const approveMutation = useApproveDeliveryPartnerMutation();
  const suspendMutation = useSuspendDeliveryPartnerMutation();

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [commission, setCommission] = useState<string>("");

  const statusLabel =
    partner?.statusText ||
    (typeof partner?.status === "number" ? DeliveryPartnerStatus[partner.status] : String(partner?.status ?? "Pending"));
  const busy = approveMutation.isPending || suspendMutation.isPending;

  const handleApprove = async () => {
    try {
      const commissionValue = commission.trim() ? Number(commission) : undefined;
      await approveMutation.mutateAsync({
        id: partnerId,
        approve: true,
        commissionPerDelivery:
          commissionValue != null && Number.isFinite(commissionValue) ? commissionValue : undefined,
      });
      toast({ title: "Delivery partner approved" });
      refetch();
    } catch (error) {
      toast({ title: "Failed to approve", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleReject = async () => {
    try {
      await approveMutation.mutateAsync({
        id: partnerId,
        approve: false,
        rejectionReason: rejectReason.trim() || "Rejected by admin",
      });
      toast({ title: "Delivery partner rejected", variant: "destructive" });
      setRejectOpen(false);
      setRejectReason("");
      refetch();
    } catch (error) {
      toast({ title: "Failed to reject", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleSuspend = async () => {
    try {
      await suspendMutation.mutateAsync({ id: partnerId, reason: "Suspended by admin" });
      toast({ title: "Delivery partner suspended", variant: "destructive" });
      refetch();
    } catch (error) {
      toast({ title: "Failed to suspend", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 rounded-xl" />
        <div className="grid gap-4 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">Delivery partner not found.</p>
        <Button variant="outline" asChild>
          <Link to="/admin/delivery">Back to delivery partners</Link>
        </Button>
      </div>
    );
  }

  const normalizedStatus = statusLabel.toLowerCase();
  const isPending = normalizedStatus === "pending";
  const isActive = normalizedStatus === "active";
  const isSuspended = normalizedStatus === "suspended";
  const isRejected = normalizedStatus === "rejected";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/delivery">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <PageHeader
          title={partner.fullName}
          description="Review driver application details before approval"
          className="min-w-0 flex-1"
        />
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          {isPending && (
            <>
              <Button size="sm" className="gap-1 bg-success hover:bg-success/90" onClick={handleApprove} disabled={busy}>
                <Check className="h-4 w-4" /> Approve
              </Button>
              <Button size="sm" variant="destructive" className="gap-1" onClick={() => setRejectOpen(true)} disabled={busy}>
                <Ban className="h-4 w-4" /> Reject
              </Button>
            </>
          )}
          {isActive && (
            <Button size="sm" variant="destructive" className="gap-1" onClick={handleSuspend} disabled={busy}>
              <Ban className="h-4 w-4" /> Suspend
            </Button>
          )}
          {(isSuspended || isRejected) && (
            <Button size="sm" className="gap-1 bg-success hover:bg-success/90" onClick={handleApprove} disabled={busy}>
              <Check className="h-4 w-4" /> {isRejected ? "Approve" : "Reactivate"}
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 pt-6">
          <Badge variant="outline" className={statusBadge[statusLabel] ?? statusBadge.Pending}>
            {statusLabel}
          </Badge>
          <span className="text-sm text-muted-foreground">Applied {formatDate(partner.createdAt)}</span>
          {partner.approvedAt ? (
            <span className="text-sm text-muted-foreground">· Approved {formatDate(partner.approvedAt)}</span>
          ) : null}
          {partner.rejectionReason ? (
            <span className="w-full text-sm text-destructive">Rejection reason: {partner.rejectionReason}</span>
          ) : null}
        </CardContent>
      </Card>

      <StatsGrid columns={3}>
        <CompactStatCard title="Deliveries" value={partner.totalDeliveries} icon={Truck} />
        <CompactStatCard title="Earnings" value={`₹${Number(partner.totalEarnings).toFixed(0)}`} icon={Wallet} />
        <CompactStatCard
          title="Rating"
          value={partner.rating > 0 ? `${partner.rating.toFixed(1)}` : "—"}
          icon={Star}
        />
      </StatsGrid>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Personal details</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailRow label="Full name" value={partner.fullName} />
            <DetailRow
              label="Phone"
              value={
                <span className="inline-flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                  {partner.phone}
                </span>
              }
            />
            <DetailRow label="Email" value={partner.email || "—"} />
            <DetailRow label="User ID" value={partner.userId} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Vehicle & service area</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailRow label="Vehicle type" value={partner.vehicleType} />
            <DetailRow label="Vehicle number" value={partner.vehicleNumber || "—"} />
            <DetailRow
              label="City / State"
              value={
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                  {partner.city}
                </span>
              }
            />
            <DetailRow label="Service radius" value={`${partner.serviceRadius} km`} />
            <DetailRow label="Service pincodes" value={partner.servicePinCodes || "—"} />
            <DetailRow label="Commission / delivery" value={`₹${partner.commissionPerDelivery}`} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">KYC documents</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailRow label="Driving license" value={partner.drivingLicenseNumber || "—"} />
            <DetailRow label="License document" value={<DocLink url={partner.drivingLicenseUrl} label="View file" />} />
            <DetailRow label="Aadhaar" value={partner.aadharNumber || "—"} />
            <DetailRow label="Aadhaar front" value={<DocLink url={partner.aadharFrontUrl} label="View file" />} />
            <DetailRow label="Aadhaar back" value={<DocLink url={partner.aadharBackUrl} label="View file" />} />
            <DetailRow label="PAN" value={partner.panNumber || "—"} />
            <DetailRow label="PAN document" value={<DocLink url={partner.panCardUrl} label="View file" />} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Bank / payout</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailRow label="Account name" value={partner.bankAccountName || "—"} />
            <DetailRow label="Account number" value={partner.bankAccountNumber || "—"} />
            <DetailRow label="IFSC" value={partner.bankIfscCode || "—"} />
            <DetailRow label="Bank name" value={partner.bankName || "—"} />
            <DetailRow label="UPI ID" value={partner.upiId || "—"} />
          </CardContent>
        </Card>
      </div>

      {isPending && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Approval options</CardTitle>
          </CardHeader>
          <CardContent className="max-w-sm space-y-2">
            <Label htmlFor="commission">Commission per delivery (optional)</Label>
            <Input
              id="commission"
              type="number"
              min={0}
              step={1}
              placeholder={`Default ₹${partner.commissionPerDelivery}`}
              value={commission}
              onChange={(e) => setCommission(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">Leave blank to keep the default commission.</p>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject delivery partner?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the application as rejected. You can share a reason for the driver.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="rejectReason">Rejection reason</Label>
            <Textarea
              id="rejectReason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Incomplete documents / invalid vehicle details"
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                void handleReject();
              }}
              disabled={busy}
            >
              Reject application
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDeliveryDetail;
